import { LoginPage, RequireAuth, SignedInRedirect } from '@frontends/feature-spg-auth';
import { BotsPage } from '@frontends/feature-spg-bots';
import { ChannelsPage } from '@frontends/feature-spg-channels';
import {
  CreateEntryFlowPage,
  EditEntryFlowPage,
  EntryFlowPage,
  EntryFlowsPage,
} from '@frontends/feature-spg-entry-flows';
import { InstancePage, InstancesPage } from '@frontends/feature-spg-instances';
import { ParticipantPage } from '@frontends/feature-spg-participants';
import {
  CreateRetentionRulePage,
  EditRetentionRulePage,
  RetentionRulePage,
  RetentionRulesPage,
} from '@frontends/feature-spg-retention-rules';
import { createBrowserRouter, Navigate } from 'react-router';
import { AppLayout } from './layout/AppLayout';
import { PATHS } from './layout/navigation';

const entryFlowHref = (id: string): string => `${PATHS.entryFlows}/${id}`;
const editEntryFlowHref = (id: string): string => `${entryFlowHref(id)}/edit`;
const instanceHref = (id: string): string => `${PATHS.instances}/${id}`;
const participantHref = (instanceId: string, participantId: string): string =>
  `${instanceHref(instanceId)}/participants/${participantId}`;
const instanceParticipantsHref = (instanceId: string): string => `${instanceHref(instanceId)}?tab=participants`;
const ruleHref = (id: string): string => `${PATHS.retentionRules}/${id}`;
const editRuleHref = (id: string): string => `${ruleHref(id)}/edit`;
const newRuleHref = (instanceId: string): string =>
  `${PATHS.newRetentionRule}?instance=${encodeURIComponent(instanceId)}`;

export const router = createBrowserRouter([
  { path: PATHS.login, element: <LoginPage /> },
  {
    element: <RequireAuth loginPath={PATHS.login} />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <SignedInRedirect fallback={PATHS.instances} /> },
          { path: PATHS.instances, element: <InstancesPage getInstanceHref={instanceHref} /> },
          {
            path: `${PATHS.instances}/:id`,
            element: (
              <InstancePage
                backHref={PATHS.instances}
                getEntryFlowHref={entryFlowHref}
                getRuleHref={ruleHref}
                getNewRuleHref={newRuleHref}
                getParticipantHref={participantHref}
              />
            ),
          },
          {
            path: `${PATHS.instances}/:instanceId/participants/:participantId`,
            element: (
              <ParticipantPage
                instancesHref={PATHS.instances}
                getInstanceParticipantsHref={instanceParticipantsHref}
                getRuleHref={ruleHref}
              />
            ),
          },
          { path: PATHS.bots, element: <BotsPage getInstanceHref={instanceHref} /> },
          { path: PATHS.channels, element: <ChannelsPage getInstanceHref={instanceHref} /> },
          {
            path: PATHS.entryFlows,
            element: <EntryFlowsPage createHref={PATHS.newEntryFlow} getFlowHref={entryFlowHref} />,
          },
          {
            path: PATHS.newEntryFlow,
            element: <CreateEntryFlowPage backHref={PATHS.entryFlows} getFlowHref={entryFlowHref} />,
          },
          {
            path: `${PATHS.entryFlows}/:id`,
            element: <EntryFlowPage backHref={PATHS.entryFlows} getEditHref={editEntryFlowHref} />,
          },
          {
            path: `${PATHS.entryFlows}/:id/edit`,
            element: <EditEntryFlowPage backHref={PATHS.entryFlows} getFlowHref={entryFlowHref} />,
          },
          {
            path: PATHS.retentionRules,
            element: (
              <RetentionRulesPage
                createHref={PATHS.newRetentionRule}
                getInstanceHref={instanceHref}
                getRuleHref={ruleHref}
              />
            ),
          },
          {
            path: PATHS.newRetentionRule,
            element: <CreateRetentionRulePage backHref={PATHS.retentionRules} getRuleHref={ruleHref} />,
          },
          {
            path: `${PATHS.retentionRules}/:id`,
            element: (
              <RetentionRulePage
                backHref={PATHS.retentionRules}
                getEditHref={editRuleHref}
                getInstanceHref={instanceHref}
                getRuleHref={ruleHref}
              />
            ),
          },
          {
            path: `${PATHS.retentionRules}/:id/edit`,
            element: <EditRetentionRulePage backHref={PATHS.retentionRules} getRuleHref={ruleHref} />,
          },
          { path: '*', element: <Navigate to={PATHS.instances} replace /> },
        ],
      },
    ],
  },
]);
